/**
 * Copyright 2020 (c) Dominik Basner, Sotec GmbH (for VDW e.V.)
 * Copyright 2020 (c) Fabian Wilhelm, Sotec GmbH (for VDW e.V.)
 * Copyright 2020-2021 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021-2022 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021-2022 (c) Skander BenHassine, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2026 (c) Alexander Uhl, ISW University of Stuttgart (for umati and VDW e.V.)
 */

import { labels } from '@umati/dashboard-common';
import { getGeodata } from 'api/geodata';
import * as d3 from 'd3';
import React, { Component, useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import Supercluster from 'supercluster';
import FrontendURLs from 'utilities/FrontendURLs';
import { getFairs, getMachines, getSoftwareList } from '../../redux/selectors';
import mapClusterMachines from '../../styles/assets/map_cluster_machines.svg';
import mapClusterMachinesOnline from '../../styles/assets/map_cluster_machines_online.svg';
import mapPinFair from '../../styles/assets/map_pin_fair.svg';
import mapPinMachineOffline from '../../styles/assets/map_pin_machine_light_magenta.svg';
import mapPinMachineOnline from '../../styles/assets/map_pin_machine_magenta.svg';
import mapPinSoftware from '../../styles/assets/map_pin_software_cyan.svg';
import FairInfoWindow from './FairInfoWindow';
import GeoBoxes from './geobox.json';
import GeodataLow from './geodataLow.json';
import MachineInfoWindow from './MachineInfoWindow';
import SwInfoWindow from './swInfoWindow';
import '../../styles/clusteredWindow.scss';
import CustomInfoWindow from './CustomInfoWindow';

function hashCode(str) {
  var hash = 0;
  for (var i = 0; i < str.length; i++) {
    var char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash;
}

// Memoizes the last call by its first argument (same semantics as memoize-one).
function memoizeLast(fn) {
  let lastArg;
  let lastResult;
  let hasLast = false;
  return (arg, ...rest) => {
    if (hasLast && arg === lastArg) {
      return lastResult;
    }
    lastArg = arg;
    lastResult = fn(arg, ...rest);
    hasLast = true;
    return lastResult;
  };
}

function dummy() {
  console.log('dummy');
}

function createClusterMarker(key, icon, lat, lng, onClick, onMouseOver, onMouseOut, cnt, markers, mid2ClustId) {
  const machineIds = markers.map((marker) => {
    mid2ClustId[marker.properties.marker.props.machineId] = 'm' + key.id;
    return marker.properties.marker.props.machineId;
  });
  return {
    key: 'm' + key.id,
    props: {
      icon,
      position: { lat, lng },
      onClick,
      onMouseOver,
      onMouseOut,
      pointCount: cnt,
      markers,
      machineIds,
    },
  };
}

function createMachineMarker(key, icon, lat, lng, onClick, onMouseOver, onMouseOut) {
  return {
    key: key.properties.marker.key,
    props: {
      icon,
      position: { lat, lng },
      onClick,
      machineIdHash: `h${hashCode(key.properties.marker.props.machineId)}`,
      machineId: key.properties.marker.props.machineId,
      onMouseOver,
      onMouseOut,
    },
  };
}

// biome-ignore lint/correctness/noUnusedVariables: Ignored already in ESLint
function projectBoxes(wwidth, wheight) {
  var projection = d3
    .geoNaturalEarth1()
    .scale(wwidth / 0.5 / Math.PI)
    .translate([wwidth / 2, wheight / 2]);
  for (var [, obj] of Object.entries(GeoBoxes)) {
    var lat = obj['sw']['lat'];
    var lng = obj['sw']['lon'];
    var [proj_lat, proj_lng] = projection([lng, lat]);
    obj['sw']['proj_lat'] = proj_lat;
    obj['sw']['proj_lng'] = proj_lng;
    lat = obj['ne']['lat'];
    lng = obj['ne']['lon'];
    [proj_lat, proj_lng] = projection([lng, lat]);
    obj['ne']['proj_lat'] = proj_lat;
    obj['ne']['proj_lng'] = proj_lng;
  }
}

function CustomMap(props) {
  const [clusterer] = useState(new Supercluster({ radius: 40, maxZoom: 16 }));
  const markers = props.markers;
  const [nonClusteredMachines, setNonClusteredMachines] = useState([]);
  const [clusterMarkers, setClusterMarkers] = useState([]);
  const ref = useRef(null);
  const vpref = useRef(null);
  const [visibleWidth, setVisibleWidth] = useState(0);
  const [Geodata, setGeodata] = useState(null);
  const [loadedGeodata, setLoadedGeodata] = useState(false);
  const [zoom] = useState({
    scaleFactor: 2,
    duration: 0.5,
  });
  const [keyf, setKeyf] = useState(null);
  const [point, setPoint] = useState({});
  const [wheeling, setWheeling] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const result = await getGeodata();
      setGeodata(result);
      setLoadedGeodata(true);
    };
    fetchData();
  }, []);

  function getStartWidth() {
    return 3840;
  }
  function getStartHeight() {
    return 2160;
  }

  const [viewBox, setViewBox] = useState({
    x: 0,
    y: 0,
    width: getStartWidth(),
    height: getStartHeight(),
  });
  const [targetViewBox, setTargetViewBox] = useState({
    x: 2270 - document.documentElement.clientWidth / 2,
    y: -1250 - document.documentElement.clientHeight / 4,
    width: getStartWidth(),
    height: getStartHeight(),
  });
  const [lasVviewBoxWidth, setLasVviewBoxWidth] = useState(document.documentElement.clientWidth);
  const [viewBoxHeight, setViewBoxHeight] = useState(document.documentElement.clientHeight);
  const [viewBoxWidth, setViewBoxWidht] = useState(document.documentElement.clientWidth);
  const [wwidth] = useState(getStartWidth());
  const [wheight] = useState(getStartHeight());
  const [prevDiff, setPrevDiff] = useState(-1);
  const [machineIdsToClusterId, setMachineIdsToClusterId] = useState({});
  const [lastTouchPos, setLastTouchPos] = useState([null, null]);

  useEffect(() => {
    if (!props.center) return;
    const projection = d3
      .geoNaturalEarth1()
      .scale(wwidth / 0.5 / Math.PI)
      .translate([wwidth / 2, wheight / 2]);
    const [px, py] = projection([props.center.lng, props.center.lat]);
    const newX = px - document.documentElement.clientWidth / 2;
    const newY = py - document.documentElement.clientHeight / 2;
    setViewBox((v) => ({ ...v, x: newX, y: newY }));
    setTargetViewBox((v) => ({ ...v, x: newX, y: newY }));
  }, [props.center]);

  useEffect(() => {
    const ZOOM_SCALE = 5;
    let zoomFactor = ZOOM_SCALE * Math.round(wwidth / viewBoxWidth);
    if (zoomFactor > 10) {
      zoomFactor = 10;
    }
    const geofeatures = [];
    props.machineMarkers.forEach((marker) => {
      const geoFeature = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [marker.props.position.lng, marker.props.position.lat],
        },
        properties: {
          name: marker.key,
          onClick: marker.props.onClick,
          onMouseOver: marker.props.onMouseOver,
          onMouseOut: marker.props.onMouseOut,
          marker: marker,
        },
      };
      geofeatures.push(geoFeature);
    });
    clusterer.load(geofeatures);
    const clusters = clusterer.getClusters([-180, -85, 180, 85], zoomFactor);

    const clusterMarkers = [];
    const nonClusteredMachines = [];
    const machineIdsToClusterId = {};
    clusters.forEach((cl) => {
      if (cl.properties.cluster) {
        const clmarkers = clusterer.getLeaves(cl.properties.cluster_id, 1000, 0);
        clusterMarkers.push(
          createClusterMarker(
            cl,
            mapClusterMachines,
            cl.geometry.coordinates[1],
            cl.geometry.coordinates[0],
            props.clusterCallback,
            dummy,
            dummy,
            cl.properties.point_count,
            clmarkers,
            machineIdsToClusterId,
          ),
        );
      } else {
        nonClusteredMachines.push(
          createMachineMarker(
            cl,
            mapPinMachineOffline,
            cl.geometry.coordinates[1],
            cl.geometry.coordinates[0],
            cl.properties.onClick,
            cl.properties.onMouseOver,
            cl.properties.onMouseOut,
          ),
        );
      }
    });
    setClusterMarkers([...clusterMarkers]);
    setMachineIdsToClusterId(machineIdsToClusterId);
    setNonClusteredMachines([...nonClusteredMachines]);
  }, [props.machineMarkers, clusterer, wwidth, viewBoxWidth]);

  useEffect(() => {
    let svg = d3.select(ref.current);
    props.onlineMachines.forEach((id) => {
      svg.select(`#h${hashCode(id)}`).attr('xlink:href', mapPinMachineOnline);

      const clusterId = machineIdsToClusterId[id];
      if (clusterId) {
        svg.select(`#${clusterId}`).attr('xlink:href', mapClusterMachinesOnline);
      }
    });
  }, [props.onlineMachines, machineIdsToClusterId]);

  const NUM_KEYFRAMES = 5;

  useEffect(() => {
    if (keyf != null && keyf.index < NUM_KEYFRAMES) {
      setViewBox({
        x: keyf.xs[keyf.index],
        y: keyf.ys[keyf.index],
        width: keyf.widths[keyf.index],
        height: keyf.heights[keyf.index],
      });
      setViewBoxHeight(keyf.heights[keyf.index]);
      setViewBoxWidht(keyf.widths[keyf.index]);
      setKeyf({ ...keyf, index: keyf.index + 1 });
    } else {
      setViewBox({ ...targetViewBox });
      setViewBoxHeight(targetViewBox.height);
      setViewBoxWidht(targetViewBox.width);
      setWheeling(false);
    }
  }, [targetViewBox, keyf, wheeling]);

  function linspace(start, stop, num, endpoint = true) {
    const div = endpoint ? num - 1 : num;
    const step = (stop - start) / div;
    return Array.from({ length: num }, (_, i) => start + step * i);
  }

  function onWheel(e) {
    if (wheeling) {
      return;
    }

    const event = e.nativeEvent;
    const svg = ref.current;
    let normalized = false;
    let delta = event.wheelDelta;
    if (delta) {
      normalized = delta % 120 == 0 ? delta / 120 : delta / 12;
    } else {
      delta = event.deltaY || event.detail || 0;
      normalized = -(delta % 3 ? delta * 10 : delta / 3);
    }
    const scaleDelta = normalized > 0 ? 1 / zoom.scaleFactor : zoom.scaleFactor;
    point.x = event.clientX;
    point.y = event.clientY;
    const startPoint = point.matrixTransform(svg.getScreenCTM().inverse());
    let newViewBox = {};
    newViewBox.x = Math.round(viewBox.x - (startPoint.x - viewBox.x) * (scaleDelta - 1));
    newViewBox.y = Math.round(viewBox.y - (startPoint.y - viewBox.y) * (scaleDelta - 1));
    newViewBox.width = Math.round(viewBoxWidth * scaleDelta);
    newViewBox.height = Math.round(viewBoxHeight * scaleDelta);
    if (newViewBox.height > wheight * 4) {
      newViewBox = viewBox;
    }
    if (newViewBox.width > wwidth * 4) {
      newViewBox = viewBox;
    }
    if (newViewBox.height < 20) {
      newViewBox = viewBox;
    }
    if (newViewBox.width < 20) {
      newViewBox = viewBox;
    }

    const heights = linspace(viewBoxHeight, newViewBox.height, NUM_KEYFRAMES);
    const xs = linspace(viewBox.x, newViewBox.x, NUM_KEYFRAMES);
    const ys = linspace(viewBox.y, newViewBox.y, NUM_KEYFRAMES);
    const widths = linspace(viewBoxWidth, newViewBox.width, NUM_KEYFRAMES);
    const keyframes = {
      widths,
      heights,
      xs,
      ys,
      index: 0,
    };
    setKeyf(keyframes);
    setWheeling(true);
    setTargetViewBox(newViewBox);
  }

  useEffect(() => {
    const svg = ref.current;
    setPoint(svg.createSVGPoint());
    const cancelWheel = (e) => e.preventDefault();
    document.body.addEventListener('wheel', cancelWheel, { passive: false });
    return () => document.body.removeEventListener('wheel', cancelWheel);
  }, [ref]);

  function valueInRange(value, min, max) {
    return value >= min && value <= max;
  }

  function isCollide(A, B) {
    const xOverlap = valueInRange(A.x, B.x, B.x + B.w) || valueInRange(B.x, A.x, A.x + A.wheelDelta);

    const yOverlap = valueInRange(A.y, B.y, B.y + B.h) || valueInRange(B.y, A.y, A.y + A.h);

    return xOverlap && yOverlap;
  }

  const onDragListener = useCallback(
    (event) => {
      if (event.pointerType === 'touch') {
        return;
      }
      let movementX = event.movementX;
      let movementY = event.movementY;
      point.x = 0;
      point.y = 0;
      const svg = ref.current;
      const startPoint = point.matrixTransform(svg.getScreenCTM().inverse());
      point.x = movementX;
      point.y = movementY;
      const endPoint = point.matrixTransform(svg.getScreenCTM().inverse());
      const browserZoomLevel = window.outerWidth / window.innerWidth;
      movementX = (startPoint.x - endPoint.x) / browserZoomLevel;
      movementY = (startPoint.y - endPoint.y) / browserZoomLevel;
      setVisibleWidth(browserZoomLevel);
      setViewBox((o) => {
        return { ...o, x: o.x + movementX, y: o.y + movementY };
      });
    },
    [point],
  );

  function onDrag() {
    props.clearMap();
    const svg = ref.current;
    svg.addEventListener('pointermove', onDragListener, { passive: true });
  }

  function onDragStop() {
    const svg = ref.current;
    setLastTouchPos([null, null]);
    svg.removeEventListener('pointermove', onDragListener);
  }

  function onTouchStart(e) {
    if (e.touches.length > 1) {
      setPrevDiff(-1);
      onDragStop();
    }
  }

  function onTouchEnd(e) {
    if (e.touches.length < 2) {
      onDrag();
    }
  }

  const midPoint = ([x1, x2], [y1, y2]) => [(x1 + x2) / 2, (y1 + y2) / 2];

  const onTouchMove = useCallback(
    (e) => {
      function onWheelTouch(scale, [midX, midY]) {
        point.x = midX;
        point.y = midY;
        const svg = ref.current;
        const startPoint = point.matrixTransform(svg.getScreenCTM().inverse());
        let newViewBox = {};
        newViewBox.x = Math.round(viewBox.x - (startPoint.x - viewBox.x) * (scale - 1));
        newViewBox.y = Math.round(viewBox.y - (startPoint.y - viewBox.y) * (scale - 1));
        newViewBox.width = Math.round(viewBoxWidth * scale);
        newViewBox.height = Math.round(viewBoxHeight * scale);

        if (newViewBox.height > wheight * 4 || newViewBox.width > wwidth * 4 || newViewBox.width < 2) {
          newViewBox.height = viewBoxHeight;
          newViewBox.width = viewBoxWidth;
          newViewBox.x = viewBox.x;
          newViewBox.y = viewBox.y;
        }
        setViewBox(newViewBox);
        setViewBoxWidht(newViewBox.width);
        setViewBoxHeight(newViewBox.height);
      }

      if (e.touches.length > 1) {
        const dist = Math.sqrt(
          Math.pow(e.touches[0].clientX - e.touches[1].clientX, 2) +
            Math.pow(e.touches[0].clientY - e.touches[1].clientY, 2),
        );
        if (prevDiff === -1 && prevDiff !== dist) {
          setPrevDiff(dist);
        } else {
          const sign = prevDiff > dist ? 1 : -1;
          onWheelTouch(
            1 + (sign * prevDiff) / dist / 20,
            midPoint([e.touches[0].clientX, e.touches[1].clientX], [e.touches[0].clientY, e.touches[1].clientY]),
          );
          setPrevDiff(dist);
        }
      } else {
        const currentTouchPos = [e.touches[0].clientX, e.touches[0].clientY];
        if (lastTouchPos[0] === null) {
          setLastTouchPos(currentTouchPos);
        } else if (lastTouchPos === currentTouchPos) {
          return;
        } else {
          let movementX = 0 - lastTouchPos[0] + currentTouchPos[0];
          let movementY = 0 - lastTouchPos[1] + currentTouchPos[1];

          if (Math.pow(movementX, 2) + Math.pow(movementY, 2) < 30) {
            return;
          }

          point.x = 0;
          point.y = 0;
          const svg = ref.current;
          const startPoint = point.matrixTransform(svg.getScreenCTM().inverse());
          point.x = movementX;
          point.y = movementY;
          const endPoint = point.matrixTransform(svg.getScreenCTM().inverse());
          const browserZoomLevel = window.outerWidth / window.innerWidth;
          movementX = (startPoint.x - endPoint.x) / browserZoomLevel;
          movementY = (startPoint.y - endPoint.y) / browserZoomLevel;
          setVisibleWidth(browserZoomLevel);
          setViewBox((o) => {
            return { ...o, x: o.x + movementX, y: o.y + movementY };
          });
          setLastTouchPos(currentTouchPos);
        }
      }
    },
    [point, viewBox.x, viewBox.y, viewBoxWidth, viewBoxHeight, wheight, wwidth, prevDiff, lastTouchPos, ref],
  );

  useEffect(() => {
    var svg = d3.select(ref.current);
    var projection = d3
      .geoNaturalEarth1()
      .scale(wwidth / 0.5 / Math.PI)
      .translate([wwidth / 2, wheight / 2]);
    const all = svg.selectAll('path');
    if (
      (lasVviewBoxWidth <= 7000 && viewBoxWidth >= 7000) ||
      (lasVviewBoxWidth >= 7000 && viewBoxWidth <= 7000) ||
      all.size() == 0 ||
      loadedGeodata === true
    ) {
      setLoadedGeodata(false);
      const geodatas = viewBoxWidth < 7000 && Geodata != null ? Geodata.features : GeodataLow.features;
      all.remove();
      svg
        .append('g')
        .selectAll('path')
        .data(geodatas)
        .enter()
        .append('path')
        .attr('id', (d) => {
          return d.properties.iso_a3;
        })
        .attr('fill', '#e8edef')
        .attr('d', d3.geoPath().projection(projection))
        .style('stroke', '#cdd8dd')
        .style('stroke-width', viewBoxWidth / wwidth);
    } else {
      all.style('stroke-width', viewBoxWidth / wwidth);
    }

    const COND_RENDER = false;
    if (COND_RENDER) {
      let noTrue = 0;
      let noFalse = 0;
      GeodataLow.features.forEach((geodatum) => {
        const box = GeoBoxes[geodatum.properties.iso_a3];
        if (box === undefined) {
          return true;
        } else {
          const height = box.sw.proj_lng - box.ne.proj_lng;
          const width = box.ne.proj_lat - box.sw.proj_lat;
          const x = box.sw.proj_lat;
          const y = box.ne.proj_lng;
          const box1 = { x, y, w: width, h: height };
          const box2 = {
            x: targetViewBox.x,
            y: targetViewBox.y,
            w: targetViewBox.width / zoom.scaleFactor,
            h: targetViewBox.height / zoom.scaleFactor,
          };
          const collide = isCollide(box1, box2);
          svg.select(`#${geodatum.properties.iso_a3}`).attr('display', collide ? 'inline' : 'none');
          collide ? noTrue++ : noFalse++;
          const allect = svg.selectAll('rect').remove();
          if (allect.size() > 0) {
            allect.remove();
          }
          /*
            svg.append('rect')
            .attr('x', box2.x)
            .attr('y', box2.y)
            .attr('width', box2.w )
            .attr('height', box2.h)
            .attr('stroke', '#ff0000')
            .attr('fill', 'transparent');
            */
          return collide;
        }
      });
    }
    if (lasVviewBoxWidth != viewBoxWidth) {
      setLasVviewBoxWidth(viewBoxWidth);
    }
  }, [wwidth, wheight, viewBoxWidth, lasVviewBoxWidth, targetViewBox, Geodata]);

  const MARKER_SCALING = 1.5;
  useEffect(() => {
    var svg = d3.select(ref.current);
    var projection = d3
      .geoNaturalEarth1()
      .scale(wwidth / 0.5 / Math.PI)
      .translate([wwidth / 2, wheight / 2]);

    svg.selectAll('image').remove();
    svg.selectAll('text').remove();

    nonClusteredMachines.forEach((marker) => {
      const pos = projection([marker.props.position.lng, marker.props.position.lat]);
      const isOnline = props.onlineMachines.includes(marker.props.machineId);
      svg
        .append('image')
        .attr('id', marker.props.machineIdHash)
        .attr('xlink:href', isOnline ? mapPinMachineOnline : mapPinMachineOffline)
        .attr('x', pos[0] - (MARKER_SCALING * 22 * (viewBoxWidth / wwidth)) / 2)
        .attr('y', pos[1] - MARKER_SCALING * 22 * (viewBoxWidth / wwidth))
        .attr('width', MARKER_SCALING * 22 * (viewBoxWidth / wwidth))
        .attr('height', MARKER_SCALING * 22 * (viewBoxWidth / wwidth))
        .on(
          'pointerdown',
          (e) => {
            const p = e.target.getBoundingClientRect();
            const domPos = [p.x, document.documentElement.clientHeight - p.y + 200];
            marker.props.onClick(domPos);
          },
          { passive: true },
        )
        .on(
          'pointerleave',
          (e) => {
            if (e.pointerType == 'touch') {
              return;
            }
            if (e.relatedTarget.tagName != 'DIV') {
              marker.props.onMouseOver([0, 0]);
            }
          },
          { passive: true },
        )
        .on(
          'mouseover',
          (e) => {
            const domPos = [
              e.srcElement.getBoundingClientRect().x,
              document.documentElement.clientHeight - e.srcElement.getBoundingClientRect().y + 200,
            ];
            marker.props.onMouseOver(domPos);
          },
          { passive: true },
        );
    });

    clusterMarkers.forEach((marker) => {
      const MARKER_SIZE = 18;
      const pos = projection([marker.props.position.lng, marker.props.position.lat]);
      let isOnline = false;
      for (let i = 0; i < marker.props.machineIds.length; i++) {
        if (props.onlineMachines.includes(marker.props.machineIds[i])) {
          isOnline = true;
          break;
        }
      }

      svg
        .append('image')
        .attr('id', marker.key)
        .attr('xlink:href', isOnline ? mapClusterMachinesOnline : mapClusterMachines)
        .attr('x', pos[0] - (MARKER_SCALING * MARKER_SIZE * (viewBoxWidth / wwidth)) / 2)
        .attr('y', pos[1] - (MARKER_SCALING * MARKER_SIZE * (viewBoxWidth / wwidth)) / 2)
        .attr('width', MARKER_SCALING * MARKER_SIZE * (viewBoxWidth / wwidth))
        .attr('height', MARKER_SCALING * MARKER_SIZE * (viewBoxWidth / wwidth))
        .on('click', (e) => {
          const p = e.target.getBoundingClientRect();
          const domPos = [p.x, document.documentElement.clientHeight - p.y + 200];
          marker.props.onClick(marker.props.markers, domPos);
        });
      svg
        .append('text')
        .style('fill', '#7A7A7A')
        .style('user-select', 'none')
        .text(marker.props.pointCount)
        .attr('x', pos[0])
        .attr('y', pos[1])
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-weight', 'bold')
        .attr('font-size', 0.4 * MARKER_SCALING * MARKER_SIZE * (viewBoxWidth / wwidth))
        .attr('height', MARKER_SCALING * MARKER_SIZE * (viewBoxWidth / wwidth))
        .on('click', (e) => {
          const p = e.target.getBoundingClientRect();
          const domPos = [p.x, document.documentElement.clientHeight - p.y + 200];
          marker.props.onClick(marker.props.markers, domPos);
        });
    });
    markers.forEach((marker) => {
      const pos = projection([marker.props.position.lng, marker.props.position.lat]);
      svg
        .append('image')
        .attr('xlink:href', marker.props.icon)
        .attr('x', pos[0] - (MARKER_SCALING * 22 * (viewBoxWidth / wwidth)) / 2)
        .attr('y', pos[1] - MARKER_SCALING * 22 * (viewBoxWidth / wwidth))
        .attr('width', MARKER_SCALING * 22 * (viewBoxWidth / wwidth))
        .attr('height', MARKER_SCALING * 22 * (viewBoxWidth / wwidth))
        .on(
          'pointerdown',
          (e) => {
            const p = e.target.getBoundingClientRect();
            const domPos = [p.x, wheight - p.y + 200];
            marker.props.onClick(domPos);
          },
          { passive: true },
        )
        .on(
          'pointerleave',
          (e) => {
            if (e.pointerType == 'touch') {
              return;
            }
            if (e.relatedTarget.tagName != 'DIV') {
              marker.props.onMouseOver([0, 0]);
            }
          },
          { passive: true },
        )
        .on(
          'mouseover',
          (e) => {
            const domPos = [
              e.srcElement.getBoundingClientRect().x,
              document.documentElement.clientHeight - e.srcElement.getBoundingClientRect().y + 200,
            ];
            marker.props.onMouseOver(domPos);
          },
          { passive: true },
        );
    });
  }, [viewBoxWidth, viewBoxHeight, markers, clusterMarkers, nonClusteredMachines, wwidth, wheight, Geodata]);

  const DEBUG = false;
  return (
    <>
      {DEBUG && (
        <div style={{ position: 'absolute', top: '80px', left: '10px' }}>
          {`wwidth=${wwidth}`}
          <br />
          {`cwidth=${document.documentElement.clientWidth}`}
          <br />
          {`vwidth=${viewBoxWidth}`}
          <br />
          {`swidth=${visibleWidth}`}
        </div>
      )}
      <svg
        id="worldMap"
        ref={ref}
        viewBox={`${viewBox?.x} ${viewBox?.y} ${viewBoxWidth} ${viewBoxHeight}`}
        height={wheight}
        width={wwidth}
        style={{
          touchAction: 'none',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          KhtmlUserSelect: 'none',
          MozUserSelect: 'none',
          MsUserSelect: 'none',
          UserSelect: 'none',
        }}
        preserveAspectRatio="xMinYMin meet"
        onTouchMove={(e) => onTouchMove(e)}
        onTouchEnd={(e) => onTouchEnd(e)}
        onTouchStart={(e) => onTouchStart(e)}
        onWheel={(e) => {
          onWheel(e, vpref);
        }}
        onMouseLeave={(e) => onDragStop(e)}
        onPointerUp={(e) => onDragStop(e)}
        onPointerDown={(e) => onDrag(e)}
      ></svg>
    </>
  );
}

//const DEVELOPMENT_MODE = false;
class WorldMap extends Component {
  constructor(props) {
    super(props);

    const center = { lat: 48, lng: 10 };
    this.state = {
      center,
      showFairInfo: false,
      showMachineInfo: false,
      showSwInfo: false,
      showClusteredMachineInfo: false,
      sw: '',
      fair: '',
      machine: '',
      clusteredMachine: [],
      machines: [],
      machinePositions: {},
      fairs: props.fairs,
      pageFinishedLoading: false,
      markerList: [],
      machineMarkerList: [],
      swInfoPos: [0, 0],
    };
  }

  getLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(this.setCenter);
    } else {
      console.log('Using geolocation is not supported.');
    }
  }

  componentDidMount() {
    this.setCenter = this.setCenter.bind(this);
    this.getLocation = this.getLocation.bind(this);
    this.updateNumberOfMachinesPerFair = this.updateNumberOfMachinesPerFair.bind(this);
    this.handleToggleClickFair = this.handleToggleClickFair.bind(this);
    this.handleToggleClickMachine = this.handleToggleClickMachine.bind(this);
    this.handelToggleClickCluster = this.handelToggleClickCluster.bind(this);
    this.handelToggleClickCluster2 = this.handelToggleClickCluster2.bind(this);

    this.componentDidUpdate(null, this.props);
    this.getLocation();
  }

  noOfMachines = memoizeLast((machines) =>
    machines.filter((machine) => machine.Online).map((machine) => machine.MachineId),
  );

  filter = memoizeLast((fairs, sws) => {
    const markerList = [];
    for (const [fairKey, fair] of Object.entries(fairs)) {
      if (!fair.Hidden) {
        markerList.push({
          key: fairKey,
          props: {
            icon: mapPinFair,
            position: fair.Position,
            onClick: (pos) => {
              this.handleToggleClickFair(fairKey, true, pos);
            },
            onMouseOver: (pos) => {
              this.handleToggleClickFair(fairKey, false, pos);
            },
            onMouseOut: (e, pos) => {
              if (this.getYOffsetFromEvent(e) > 5) this.handleToggleClickFair(fairKey, false, pos);
            },
          },
        });
      }
    }

    for (const sw in sws) {
      if (sws?.[sw]?.Positions?.offsite) {
        markerList.push({
          key: sw,
          props: {
            icon: mapPinSoftware,
            position: sws[sw].Positions.offsite,
            onClick: (pos) => {
              this.handleToggleClickSoftware(sw, true, pos);
            },
            onMouseOver: (pos) => {
              this.handleToggleClickSoftware(sw, false, pos);
            },
            onMouseOut: (e, pos) => {
              if (this.getYOffsetFromEvent(e) > 5) this.handleToggleClickSoftware(sw, false, pos);
            },
          },
        });
      }
    }
    return markerList;
  });

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.pageFinishedLoading || prevState.machines.length !== this.props.machines.length) {
      const machines = this.props.machines;
      const positions = new Set();
      const positionsToMachines = {};
      const machinePositions = prevState.machinePositions || [];

      const k = 0.004;
      const a = 0.003;
      const y = (phi) => a * (Math.E ^ (k * phi)) * Math.sin(phi);
      const x = (phi) => a * (Math.E ^ (k * phi)) * Math.cos(phi);

      for (const machineIt in machines) {
        if (machines[machineIt].Positions && machines[machineIt].Positions.offsite) {
          const position = { ...machines[machineIt].Positions.offsite };
          const positionJSON = JSON.stringify(machines[machineIt].Positions.offsite);
          if (positions.has(positionJSON)) {
            positionsToMachines[positionJSON] = positionsToMachines[positionJSON] + 1;
          } else {
            positionsToMachines[positionJSON] = -1;
            positions.add(positionJSON);
          }

          if (positionsToMachines[positionJSON] !== -1) {
            const phi = (positionsToMachines[positionJSON] * Math.PI) / 3;
            position.lat = position.lat + x(phi);
            position.lng = position.lng + y(phi);
          }

          machines[machineIt].Positions.offsite = position;
          const machineId = machines[machineIt].MachineId;
          machinePositions[machineId] = position;
        }
      }

      const machineMarkerList = [];
      this.updateNumberOfMachinesPerFair(this.props);

      for (const machineIt in this.props.machines) {
        const machineKey = 'machine' + machineIt;
        const icon = this.props.machines[machineIt].Online ? mapPinMachineOnline : mapPinMachineOffline;
        const machineId = this.props.machines[machineIt].MachineId;
        const position = this.state.machinePositions[machineId];
        if (position) {
          const position = this.state.machinePositions[machineId];
          machineMarkerList.push({
            key: machineKey,
            props: {
              icon: icon,
              position: position,
              machineId: this.props.machines[machineIt].MachineId,
              isOnline: this.props.machines[machineIt].Online,
              onClick: (pos) => {
                this.handleToggleClickMachine(machineIt, position, true, pos);
              },
              onMouseOver: (pos) => {
                this.handleToggleClickMachine(machineIt, null, false, pos);
              },
              onMouseOut: (e, pos) => {
                if (this.getYOffsetFromEvent(e) > 5) {
                  this.handleToggleClickMachine(machineIt, null, false, pos);
                }
              },
            },
          });
        }
      }

      const markerList = [];
      for (const [fairKey, fair] of Object.entries(this.props.fairs)) {
        if (!fair.Hidden) {
          markerList.push({
            key: fairKey,
            props: {
              icon: mapPinFair,
              position: fair.Position,
              onClick: (pos) => {
                this.handleToggleClickFair(fairKey, true, pos);
              },
              onMouseOver: (pos) => {
                this.handleToggleClickFair(fairKey, false, pos);
              },
              onMouseOut: (e, pos) => {
                if (this.getYOffsetFromEvent(e) > 5) this.handleToggleClickFair(fairKey, false, pos);
              },
            },
          });
        }
      }

      const sws = this.props.software;
      for (const sw in sws) {
        if (sws?.[sw]?.Positions?.offsite) {
          markerList.push({
            key: sw,
            props: {
              icon: mapPinSoftware,
              position: sws[sw].Positions.offsite,
              onClick: (pos) => {
                this.handleToggleClickSoftware(sw, true, pos);
              },
              onMouseOver: (pos) => {
                this.handleToggleClickSoftware(sw, false, pos);
              },
              onMouseOut: (e, pos) => {
                if (this.getYOffsetFromEvent(e) > 5) this.handleToggleClickSoftware(sw, false, pos);
              },
            },
          });
        }
      }
      this.setState({
        ...this.state,
        machines,
        machinePositions,
        pageFinishedLoading: true,
        machineMarkerList,
        markerList,
      });
    }
  }

  setCenter(position) {
    const center = { lat: position.coords.latitude, lng: position.coords.longitude };
    console.log('[WorldMap] Browser location:', center);
    this.setState({ center });
  }

  updateNumberOfMachinesPerFair(props) {
    for (const machineIndex in props.machines) {
      const machine = props.machines[machineIndex];
      if (machine.Positions?.offsite) {
        machine.Position = machine.Positions.offsite;
      }
      if (machine.Data.Model?.text) {
        machine.Name = machine.Data.Model.text;
      }
    }
    for (const fairIndex in props.fairs) {
      const fair = props.fairs[fairIndex];
      fair.NumberOfMachines = 0;
      for (const machineIndex in props.machines) {
        const machine = props.machines[machineIndex];
        if (machine.Positions?.[fairIndex]) {
          fair.NumberOfMachines = fair.NumberOfMachines + 1;
        }
      }
    }
  }

  getFairWindow() {
    const newMachinesFiltered = this.props.machines.filter(
      (machine) => this.state.fair !== undefined && machine.Positions?.[this.state.fair],
    );
    let onlineMachines = 0;
    const fair = this.props.fairs[this.state.fair];

    fair.NumberOfMachines = newMachinesFiltered.length;
    newMachinesFiltered.forEach((machine) => {
      if (machine.Online) {
        onlineMachines = onlineMachines + 1;
      }
    });

    const additionalWindowInfo = (
      <div>
        {fair.From && fair.To && (
          <p className="h6">
            {fair.From} - {fair.To}
          </p>
        )}
        <p className="h6">
          {fair.NumberOfMachines || 0} {labels.machines}
        </p>
        <p className="h6">
          ({labels.online}: {onlineMachines})
        </p>
      </div>
    );
    return (
      <FairInfoWindow
        {...fair}
        onCloseClick={() => {
          this.handleToggleClickFair(this.state.fair, false);
        }}
        onMouseLeave={() => {
          this.handleToggleClickFair(this.state.fair, false);
        }}
      >
        {additionalWindowInfo}
      </FairInfoWindow>
    );
  }

  getMachineWindow() {
    const index = this.state.machine;
    const machine = this.state.machines[index];
    const position = this.state.machinePositions[machine.MachineId];

    return (
      <MachineInfoWindow
        {...machine}
        OverridePosition={position}
        onCloseClick={() => {
          this.handleToggleClickMachine(index, position);
        }}
        onMouseLeave={() => {
          this.handleToggleClickMachine(index);
        }}
      />
    );
  }

  getSwWindow() {
    const index = this.state.sw;
    const sw = this.props.software[index];
    return (
      <SwInfoWindow
        {...sw}
        Path={FrontendURLs.getSoftware(index)}
        onCloseClick={() => {
          this.handleToggleClickSoftware(index, false);
        }}
        onMouseLeave={() => {
          this.handleToggleClickSoftware(index, false);
        }}
      />
    );
  }

  scrollRef = React.createRef();
  handleScroll = (direction) => {
    const container = this.scrollRef.current;
    if (container) {
      const { scrollLeft, clientWidth, scrollWidth } = container;
      const scrollAmount = container.clientWidth;
      container.scrollBy({
        left: direction === 'next' ? scrollAmount : -scrollAmount,
        behavior: 'smooth',
      });
      if (direction === 'next' && scrollLeft + clientWidth >= scrollWidth - 10) {
        container.scrollTo({ left: 0, behavior: 'smooth' });
      } else if (direction === 'prev' && scrollLeft <= 10) {
        container.scrollTo({ left: scrollWidth, behavior: 'smooth' });
      }
    }
  };

  getClusteredMachineWindow() {
    return (
      <div className="carousel-container">
        <div className="carousel-viewport" ref={this.scrollRef}>
          <div className="carousel-track">
            {this.state.clusteredMachine.map((item) => {
              const machine = this.state.machines[item];
              return (
                <div key={item} className="machine-slide">
                  <MachineInfoWindow
                    {...machine}
                    isCluster={true}
                    OverridePosition={this.state.machinePositions[machine.MachineId]}
                    InfoWindow
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className="carousel-controls">
          <button className="nav-btn" onClick={() => this.handleScroll('prev')}>
            &lt;
          </button>
          <button className="nav-btn" onClick={() => this.handleScroll('next')}>
            {' '}
            &gt;
          </button>
        </div>
      </div>
    );
  }

  handleToggleClickFair(fair, moveToCenter, pos = [0, 0]) {
    const center = this.props.fairs[fair].Position;
    if (this.state.fair === fair) {
      this.setState((prevState) => ({
        showFairInfo: !prevState.showFairInfo,
        showClusteredMachineInfo: false,
        showMachineInfo: false,
        showSwInfo: false,
        center: moveToCenter ? center : this.state.center,
        swInfoPos: pos,
      }));
    } else {
      this.setState({
        showFairInfo: true,
        showClusteredMachineInfo: false,
        showMachineInfo: false,
        showSwInfo: false,
        fair,
        center: moveToCenter ? center : this.state.center,
        swInfoPos: pos,
      });
    }
  }

  handleToggleClickMachine(machineIt, machinePos, moveToCenter, pos = [0, 0]) {
    const center = machinePos;
    if (this.state.machine === machineIt) {
      this.setState((prevState) => ({
        showMachineInfo: !prevState.showMachineInfo,
        showClusteredMachineInfo: false,
        showFairInfo: false,
        showSwInfo: false,
        center: moveToCenter ? center : this.state.center,
        swInfoPos: pos,
      }));
    } else {
      this.setState({
        showMachineInfo: true,
        showClusteredMachineInfo: false,
        showFairInfo: false,
        showSwInfo: false,
        machine: machineIt,
        center: moveToCenter ? center : this.state.center,
        swInfoPos: pos,
      });
    }
  }

  handleToggleClickSoftware(sw, moveToCenter, pos = [0, 0]) {
    const center = this.props.software[sw].Positions.offsite;
    if (this.state.sw === sw) {
      this.setState((prevState) => {
        return {
          showSwInfo: !prevState.showSwInfo,
          showClusteredMachineInfo: false,
          showMachineInfo: false,
          showFairInfo: false,
          center: moveToCenter ? center : this.state.center,
          swInfoPos: pos,
        };
      });
    } else {
      this.setState({
        showSwInfo: true,
        showClusteredMachineInfo: false,
        showMachineInfo: false,
        showFairInfo: false,
        sw: sw,
        center: moveToCenter ? center : this.state.center,
        swInfoPos: pos,
      });
    }
  }

  calculateMarkerStyle(markers, numStyles) {
    markers = markers || [];
    const onlineIdx = 2;
    const offlineIdx = 1;
    const onlineIcon = mapPinMachineOnline;
    let idx = offlineIdx;
    markers.forEach((marker) => {
      if (marker.icon === onlineIcon) {
        idx = onlineIdx;
      }
    });
    if (idx > numStyles) {
      idx = numStyles;
    }
    return { text: markers.length, index: idx };
  }

  handelToggleClickCluster(cluster, machineMarkerList, pos = [0, 0]) {
    // contain indexs of clustered machines
    const clusteredMachineIt = [];
    const clusteredMarkerArray = cluster.getMarkers();
    const markerClusterPosition = { lat: cluster.getCenter().lat(), lng: cluster.getCenter().lng() };

    clusteredMarkerArray.forEach((marker) => {
      const markerLatitude = marker.getPosition().lat();
      const markerLongitude = marker.getPosition().lng();
      const filteredArray = machineMarkerList.filter(
        (item) => (item.props.position.lat === markerLatitude) & (item.props.position.lng === markerLongitude),
      );
      const index = filteredArray[0].key.substring(7);
      clusteredMachineIt.push(index);
    });

    const center = markerClusterPosition;
    if (this.state.clusteredMachine === clusteredMachineIt) {
      this.setState((prevState) => ({
        showClusteredMachineInfo: !prevState.showClusteredMachineInfo,
        showMachineInfo: false,
        showFairInfo: false,
        showSwInfo: false,
        center: center,
        swInfoPos: pos,
      }));
    } else {
      this.setState({
        showClusteredMachineInfo: true,
        showMachineInfo: false,
        showFairInfo: false,
        showSwInfo: false,
        clusteredMachine: clusteredMachineIt,
        center: center,
        swInfoPos: pos,
      });
    }
  }

  handelToggleClickCluster2(machineMarkerList, pos = [0, 0]) {
    const clusteredMachineIt = machineMarkerList.map((marker) => marker.properties.marker.key.substring(7));

    if (this.state.clusteredMachine === clusteredMachineIt) {
      this.setState((prevState) => ({
        showClusteredMachineInfo: !prevState.showClusteredMachineInfo,
        showMachineInfo: false,
        showFairInfo: false,
        showSwInfo: false,
        swInfoPos: pos,
      }));
    } else {
      this.setState({
        showClusteredMachineInfo: true,
        showMachineInfo: false,
        showFairInfo: false,
        showSwInfo: false,
        clusteredMachine: clusteredMachineIt,
        swInfoPos: pos,
      });
    }
  }

  // e.domEvent.originalTarget is only available in firefox, e.domEvent.layerY has different reference points in chrome and firefox.
  getYOffsetFromEvent = (e) => {
    if (e.domEvent) {
      return e.domEvent.originalTarget
        ? e.domEvent.y - e.domEvent.originalTarget.getBoundingClientRect().y
        : e.domEvent.layerY;
    } else {
      return 0;
    }
  };

  render() {
    const markerList = this.filter(this.props.fairs, this.props.software);
    const fairWindow = this.state.showFairInfo ? this.getFairWindow() : null;
    const machineInfo =
      this.state.showMachineInfo && !this.state.showClusteredMachineInfo ? this.getMachineWindow() : null;
    const swInfo = this.state.showSwInfo ? this.getSwWindow() : null;
    const clusteredMachineInfo = this.state.showClusteredMachineInfo ? this.getClusteredMachineWindow() : null;
    const onlineMachines = this.noOfMachines(this.props.machines);
    return (
      <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', touchAction: 'none' }}>
        <CustomMap
          center={this.state.center}
          onlineMachines={onlineMachines}
          markers={markerList}
          machineMarkers={this.state.machineMarkerList}
          clearMap={() => {
            this.setState({
              showClusteredMachineInfo: false,
              showMachineInfo: false,
              showFairInfo: false,
              showSwInfo: false,
            });
          }}
          clusterCallback={this.handelToggleClickCluster2}
        ></CustomMap>
        <div
          style={{ position: 'absolute', bottom: this.state.swInfoPos[1] - 200, left: this.state.swInfoPos[0] - 80 }}
        >
          {fairWindow}
          {machineInfo}
          {swInfo}
          {this.state.showClusteredMachineInfo && (
            <CustomInfoWindow onCloseClick={() => this.setState({ showClusteredMachineInfo: false })}>
              {clusteredMachineInfo}
            </CustomInfoWindow>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    machines: getMachines(state),
    fairs: getFairs(state),
    software: getSoftwareList(state),
  };
};

export default connect(mapStateToProps)(WorldMap);
